import { env } from '@/lib/env'

import { getViewAs, isLive, VIEW_AS_HEADER, viewAsHeaderFor } from '@/staff/view-as'

import type { ProblemDetail } from './types'

/**
 * Central HTTP client for `/api/**`.
 *
 * Responsibilities, in one place:
 *  - prefixing the configured API base
 *  - attaching `Authorization: Bearer <access_token>` to authenticated calls
 *  - transparently refreshing an expired token and retrying once on 401
 *  - surfacing a single, typed {@link ApiError} for every failure mode
 *
 * The auth layer injects its token accessors via {@link configureApiClient};
 * nothing else in the app calls `fetch` against the API.
 */

export class ApiError extends Error {
  readonly status: number
  readonly problem: ProblemDetail | undefined

  constructor(status: number, message: string, problem?: ProblemDetail) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.problem = problem
  }

  /** Network failure, DNS error, CORS block — no HTTP response at all. */
  /** The staff API's machine-readable refusal code, when the problem carries one. */
  get code(): string | undefined {
    return this.problem?.code
  }

  get isNetworkError(): boolean {
    return this.status === 0
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }

  get isForbidden(): boolean {
    return this.status === 403
  }

  get isNotFound(): boolean {
    return this.status === 404
  }
}

export interface ApiClientHooks {
  /** Returns a currently-valid access token, refreshing it if necessary. */
  getAccessToken: (forceRefresh: boolean) => Promise<string | undefined>
  /** Called after a 401 survives a token refresh — the session is unrecoverable. */
  onUnauthorized: () => void
}

const noHooks: ApiClientHooks = {
  getAccessToken: async () => undefined,
  onUnauthorized: () => undefined,
}

let hooks: ApiClientHooks = noHooks

export function configureApiClient(next: ApiClientHooks): void {
  hooks = next
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** Serialised as JSON when present. */
  body?: unknown
  /** Set false for the public endpoints (`/auth/register`, `/auth/verify`). */
  authenticated?: boolean
  signal?: AbortSignal
  /** Overrides the default `application/json` Accept header. */
  accept?: string
}

function resolveUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${env.apiBaseUrl}${suffix}`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function toProblemDetail(value: unknown): ProblemDetail | undefined {
  if (!isRecord(value)) return undefined
  const problem: ProblemDetail = {}
  if (typeof value['type'] === 'string') problem.type = value['type']
  if (typeof value['title'] === 'string') problem.title = value['title']
  if (typeof value['status'] === 'number') problem.status = value['status']
  if (typeof value['detail'] === 'string') problem.detail = value['detail']
  if (typeof value['instance'] === 'string') problem.instance = value['instance']
  if (typeof value['message'] === 'string') problem.message = value['message']
  if (typeof value['error'] === 'string') problem.error = value['error']
  if (typeof value['code'] === 'string') problem.code = value['code']
  if (typeof value['limit'] === 'number') problem.limit = value['limit']
  if (typeof value['refundable_micros'] === 'number') problem.refundable_micros = value['refundable_micros']
  if (typeof value['invoice_id'] === 'string') problem.invoice_id = value['invoice_id']
  if (typeof value['upgrade_plan'] === 'string' || value['upgrade_plan'] === null) {
    problem.upgrade_plan = value['upgrade_plan']
  }
  return problem
}

function problemMessage(status: number, problem: ProblemDetail | undefined): string {
  const candidate = problem?.detail ?? problem?.message ?? problem?.title ?? problem?.error
  if (candidate !== undefined && candidate.trim().length > 0) return candidate
  return defaultMessageForStatus(status)
}

function defaultMessageForStatus(status: number): string {
  switch (status) {
    case 400:
      return 'The request was rejected as invalid.'
    case 401:
      return 'Your session has expired. Sign in again to continue.'
    case 403:
      return 'You do not have access to this resource.'
    case 404:
      return 'Not found.'
    case 409:
      return 'That conflicts with something that already exists.'
    case 410:
      return 'This link has expired or has already been used.'
    case 429:
      return 'Too many requests. Wait a moment and try again.'
    default:
      return status >= 500
        ? 'The saltseed API is not responding correctly. Try again shortly.'
        : `Request failed with status ${status}.`
  }
}

async function readErrorBody(response: Response): Promise<ProblemDetail | undefined> {
  const contentType = response.headers.get('content-type') ?? ''
  try {
    if (contentType.includes('json')) {
      const parsed: unknown = await response.json()
      return toProblemDetail(parsed)
    }
    const text = await response.text()
    return text.trim().length > 0 ? { detail: text.slice(0, 500) } : undefined
  } catch {
    return undefined
  }
}

async function performRequest(path: string, options: RequestOptions, forceRefresh: boolean): Promise<Response> {
  const { method = 'GET', body, authenticated = true, signal, accept } = options

  const session = getViewAs()
  if (
    method !== 'GET' &&
    session !== null &&
    isLive(session.expiresAt) &&
    !path.startsWith('/staff') &&
    authenticated &&
    !path.startsWith('/auth')
  ) {
    throw new ApiError(403, 'You are viewing this account as staff, read-only. End the view to make changes.', {
      code: 'impersonation_read_only',
    })
  }

  const headers = new Headers()
  headers.set('Accept', accept ?? 'application/json')
  if (body !== undefined) headers.set('Content-Type', 'application/json')

  // Read-only view-as (S13): a live session adds the account header to console GETs and nothing
  // else. A write is refused here before it leaves the browser; the server refuses it too.
  const viewAsAccount = viewAsHeaderFor(method, path, session)
  if (viewAsAccount !== undefined) headers.set(VIEW_AS_HEADER, viewAsAccount)

  if (authenticated) {
    const token = await hooks.getAccessToken(forceRefresh)
    if (token !== undefined) headers.set('Authorization', `Bearer ${token}`)
  }

  const init: RequestInit = {
    method,
    headers,
    credentials: 'omit',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal === undefined ? {} : { signal }),
  }

  try {
    return await fetch(resolveUrl(path), init)
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError(0, 'Could not reach the saltseed API. Check your connection.')
  }
}

/** Runs the request, refreshing the token and retrying once on a 401. */
async function requestRaw(path: string, options: RequestOptions): Promise<Response> {
  let response = await performRequest(path, options, false)

  if (response.status === 401 && (options.authenticated ?? true)) {
    response = await performRequest(path, options, true)
    if (response.status === 401) {
      hooks.onUnauthorized()
    }
  }

  if (!response.ok) {
    const problem = await readErrorBody(response)
    throw new ApiError(response.status, problemMessage(response.status, problem), problem)
  }

  return response
}

/** JSON request. `T` is asserted, not validated — types mirror the contract. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await requestRaw(path, options)
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }
  const text = await response.text()
  if (text.trim().length === 0) return undefined as T
  try {
    return JSON.parse(text) as T
  } catch {
    throw new ApiError(response.status, 'The API returned a malformed JSON response.')
  }
}

/** Request that returns a plain-text / script body (the tooling installers). */
export async function requestText(path: string, options: RequestOptions = {}): Promise<string> {
  const response = await requestRaw(path, { accept: 'text/plain, */*', ...options })
  return response.text()
}

/** Request that returns the raw body plus the server-suggested filename. */
export async function requestDownload(
  path: string,
  options: RequestOptions = {},
): Promise<{ blob: Blob; filename: string | undefined }> {
  const response = await requestRaw(path, { accept: '*/*', ...options })
  const blob = await response.blob()
  const disposition = response.headers.get('content-disposition')
  return { blob, filename: parseFilename(disposition) }
}

function parseFilename(disposition: string | null): string | undefined {
  if (disposition === null) return undefined
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
  const utf8Value = utf8?.[1]
  if (utf8Value !== undefined) {
    try {
      return decodeURIComponent(utf8Value)
    } catch {
      return undefined
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition)
  return plain?.[1]
}

/** Turns any thrown value into a message suitable for an error banner. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message
  if (error instanceof Error) return error.message
  return 'Something went wrong.'
}
